import React from 'react';

const SubscriptionModal = ({
  isOpen,
  onClose,
  user,
  subscriptionData,
  isLoadingSubscription,
  handleDownloadInvoice,
  usersCount = 0,
  assetsCount = 0,
}) => {
  if (!isOpen) return null;

  const planTitle =
    subscriptionData?.plan_name ||
    user?.plan_name ||
    (user?.role === 'superadmin' ? 'Master Platform Node' : 'Professional Plan');
  const planPrice = subscriptionData?.plan_price || user?.plan_price || '$149/mo';
  const billingInterval = subscriptionData?.plan_billing_cycle || user?.plan_billing_cycle || 'monthly';
  const planStatus = subscriptionData?.plan_status || user?.plan_status || 'active';

  const currentUsersCount =
    subscriptionData?.current_users !== undefined ? subscriptionData.current_users : usersCount;
  const maxUsersCount = subscriptionData?.user_limit || user?.user_limit || 15;
  const userPercent = Math.min(100, Math.round((currentUsersCount / maxUsersCount) * 100));

  const currentAssetsCount =
    subscriptionData?.current_assets !== undefined ? subscriptionData.current_assets : assetsCount;
  const maxAssetsCount = subscriptionData?.asset_limit || user?.asset_limit || 100;
  const assetPercent = Math.min(100, Math.round((currentAssetsCount / maxAssetsCount) * 100));

  const invoiceList = subscriptionData?.invoices || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="fixed inset-0" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 animate-fadeIn text-slate-800">
        <div className="p-4 px-5 border-b border-slate-100 bg-blue-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-white/20 text-white flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Subscription & Invoices</h3>
              <p className="text-[11px] text-blue-100">Plan quotas, billing cycle & payment receipts</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xs"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4 text-xs max-h-[80vh] overflow-y-auto">
          {/* Active Plan Card */}
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                  CURRENT PLAN
                </span>
                <h4 className="text-lg font-extrabold text-slate-900 mt-0.5">{planTitle}</h4>
              </div>
              <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                {planStatus.toUpperCase()} TIER
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/60">
              <div>
                <span className="text-slate-400 font-semibold block text-[11px]">Billing Amount</span>
                <span className="font-extrabold text-slate-800 text-sm">{planPrice}</span>
              </div>
              <div>
                <span className="text-slate-400 font-semibold block text-[11px]">Renewal Interval</span>
                <span className="font-bold text-slate-800 text-sm capitalize">{billingInterval}</span>
              </div>
            </div>
          </div>

          {/* Quota Usage */}
          <div className="space-y-2.5">
            <h5 className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">
              Resource Quotas & Capacity
            </h5>
            <div className="space-y-2">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70">
                <div className="flex justify-between items-center mb-1 font-semibold text-slate-700">
                  <span>User Capacity Cap</span>
                  <span className="font-bold text-blue-700">
                    {currentUsersCount} / {maxUsersCount} Users ({userPercent}%)
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full transition-all ${userPercent >= 90 ? 'bg-red-500' : 'bg-blue-600'}`}
                    style={{ width: `${userPercent}%` }}
                  />
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70">
                <div className="flex justify-between items-center mb-1 font-semibold text-slate-700">
                  <span>Hardware Fleet Cap</span>
                  <span className="font-bold text-violet-700">
                    {currentAssetsCount} / {maxAssetsCount} Assets ({assetPercent}%)
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full transition-all ${assetPercent >= 90 ? 'bg-red-500' : 'bg-violet-600'}`}
                    style={{ width: `${assetPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Invoices List */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <h5 className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">
                Recent Billing Receipts
              </h5>
              {isLoadingSubscription && <span className="text-[10px] text-slate-400">Refreshing...</span>}
            </div>

            <div className="divide-y divide-slate-100 border border-slate-200/70 rounded-xl overflow-hidden">
              {invoiceList.length > 0 ? (
                invoiceList.map((inv) => (
                  <div
                    key={inv.id}
                    className="p-2.5 px-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div>
                      <div className="font-bold text-slate-800 flex items-center gap-1.5">
                        <span>#{inv.invoice_number}</span>
                        {inv.is_current && (
                          <span className="text-[9px] font-extrabold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.2 rounded">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 font-medium">
                        {inv.period} • {inv.plan_name} • {inv.amount}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-200">
                        {inv.status}
                      </span>
                      <button
                        onClick={() => handleDownloadInvoice(inv.id)}
                        className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-[10px] transition-colors shadow-2xs"
                        title="Print or download official receipt"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-slate-400 text-[11px]">
                  No invoices found for this billing cycle.
                </div>
              )}
            </div>
          </div>

          <div className="pt-2 text-center">
            <p className="text-[11px] text-slate-400 font-medium">
              To upgrade your tier quotas or switch to annual billing, contact your account admin or reach out to{' '}
              <a href="mailto:support@nexbond.in" className="text-blue-600 font-bold hover:underline">
                support@nexbond.in
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
